import React, { useEffect, useRef } from "react";

const CONFETTI_COLORS = ["#4f9ef8", "#2563eb", "#34d399", "#fbbf24", "#f472b6", "#a78bfa", "#fb923c"];

/** Full-viewport confetti burst. Renders nothing (opacity 0, no particles) while `active` is
 * false — set it true for one moment to fire a single burst. */
function Confetti({ active }) {
  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  const particles = useRef([]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");

    const setCanvasSize = () => {
      const w = window.innerWidth;
      const h = window.visualViewport?.height || window.innerHeight;
      canvas.width = w;
      canvas.height = h;
    };

    if (!active) {
      cancelAnimationFrame(rafRef.current);
      particles.current = [];
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      return;
    }

    setCanvasSize();

    const onResize = () => setCanvasSize();
    window.addEventListener("resize", onResize);
    window.addEventListener("orientationchange", onResize);
    window.visualViewport?.addEventListener?.("resize", onResize);

    particles.current = Array.from({ length: 180 }, () => ({
      x: Math.random() * canvas.width,
      y: -20 - Math.random() * 60,
      r: 4 + Math.random() * 6,
      color: CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)],
      vx: (Math.random() - 0.5) * 6,
      vy: 2.5 + Math.random() * 5,
      rot: Math.random() * 360,
      rotV: (Math.random() - 0.5) * 10,
      shape: Math.random() > 0.5 ? "rect" : "circle",
      alpha: 1,
    }));

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles.current.forEach((p) => {
        p.x += p.vx;
        p.y += p.vy;
        p.rot += p.rotV;
        p.vy += 0.1;
        p.alpha = Math.max(0, p.alpha - 0.007);
        ctx.save();
        ctx.globalAlpha = p.alpha;
        ctx.translate(p.x, p.y);
        ctx.rotate((p.rot * Math.PI) / 180);
        ctx.fillStyle = p.color;
        if (p.shape === "rect") {
          ctx.fillRect(-p.r, -p.r * 0.5, p.r * 2, p.r);
        } else {
          ctx.beginPath();
          ctx.arc(0, 0, p.r, 0, Math.PI * 2);
          ctx.fill();
        }
        ctx.restore();
      });
      particles.current = particles.current.filter((p) => p.alpha > 0 && p.y < canvas.height + 60);
      if (particles.current.length > 0) rafRef.current = requestAnimationFrame(draw);
      else ctx.clearRect(0, 0, canvas.width, canvas.height);
    };

    rafRef.current = requestAnimationFrame(draw);
    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onResize);
      window.visualViewport?.removeEventListener?.("resize", onResize);
    };
  }, [active]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: "fixed",
        inset: 0,
        pointerEvents: "none",
        zIndex: 9999,
        opacity: active ? 1 : 0,
        transition: "opacity 0.4s",
      }}
    />
  );
}

export default Confetti;
